import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Globe,
  ShieldCheck,
  Search,
} from "lucide-react";
import {
  getPayfastWebhookStatus,
  setPayfastMode,
  getSignedPayloadTrace,
} from "@/lib/api/payfast-admin.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/payfast-webhook",
)({
  head: () => ({ meta: [{ title: "PayFast Webhook Status — Admin" }] }),
  component: PayfastWebhookAdmin,
});

function Pill({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "bad" | "muted";
  children: React.ReactNode;
}) {
  const cls = {
    ok: "bg-emerald-100 text-emerald-800",
    warn: "bg-amber-100 text-amber-800",
    bad: "bg-rose-100 text-rose-800",
    muted: "bg-slate-100 text-slate-700",
  }[tone];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {children}
    </span>
  );
}

function PayfastWebhookAdmin() {
  const router = useRouter();
  const fetchStatus = useServerFn(getPayfastWebhookStatus);
  const setMode = useServerFn(setPayfastMode);
  const fetchTrace = useServerFn(getSignedPayloadTrace);
  const [traceRef, setTraceRef] = useState("");
  const [trace, setTrace] = useState<any>(null);

  const statusQuery = useQuery({
    queryKey: ["payfast-webhook-status"],
    queryFn: () => fetchStatus(),
    refetchInterval: 20_000,
  });

  const modeMut = useMutation({
    mutationFn: (mode: "sandbox" | "live") => setMode({ data: { mode } }),
    onSuccess: (r) => {
      toast.success(`PayFast mode set to ${r.mode}`);
      router.invalidate();
      statusQuery.refetch();
    },
    onError: (e: any) => toast.error("Failed to switch mode", { description: e?.message }),
  });

  const traceMut = useMutation({
    mutationFn: (reference: string) => fetchTrace({ data: { reference } }),
    onSuccess: (r) => {
      setTrace(r);
      if (!r.trace) toast.info("No signed-payload trace stored for that reference yet.");
    },
    onError: (e: any) => toast.error("Lookup failed", { description: e?.message }),
  });

  const s = statusQuery.data;

  return (
    <div className="container-narrow space-y-6 py-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-navy">PayFast Webhook Status</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live reachability check for the ITN notify URL, runtime mode toggle, and a
            payload-trace lookup for retryable checkout diagnostics.
          </p>
        </div>
        <button
          onClick={() => statusQuery.refetch()}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm hover:bg-secondary/40"
        >
          <RefreshCw className={`h-4 w-4 ${statusQuery.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {statusQuery.isLoading ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading…
        </div>
      ) : !s ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800">
          Could not load status.
        </div>
      ) : (
        <>
          <section className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <Globe className="h-4 w-4" /> notify_url reachability
              </div>
              <p className="mt-2 break-all text-sm text-navy">{s.notifyUrl}</p>
              <div className="mt-3 flex items-center gap-2">
                {s.reachable ? (
                  <Pill tone="ok">
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Reachable (HTTP {s.reachStatus})
                  </Pill>
                ) : (
                  <Pill tone="bad">
                    <AlertCircle className="mr-1 h-3 w-3" /> Unreachable
                    {s.reachStatus ? ` (HTTP ${s.reachStatus})` : ""}
                  </Pill>
                )}
              </div>
              {s.reachError ? (
                <p className="mt-2 text-xs text-rose-700">{s.reachError}</p>
              ) : null}
              <p className="mt-2 text-[11px] text-muted-foreground">
                A GET to this URL returns 405 because the handler only accepts POST — that's
                expected and still proves reachability.
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <ShieldCheck className="h-4 w-4" /> Runtime mode
              </div>
              <p className="mt-2 font-display text-xl text-navy">
                {s.mode === "live" ? "LIVE" : "SANDBOX"}
              </p>
              <p className="text-xs text-muted-foreground">
                Process URL: <span className="break-all">{s.processUrl}</span>
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  disabled={s.mode === "sandbox" || modeMut.isPending}
                  onClick={() => modeMut.mutate("sandbox")}
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 disabled:opacity-50"
                >
                  Switch to Sandbox
                </button>
                <button
                  disabled={s.mode === "live" || modeMut.isPending}
                  onClick={() => {
                    if (confirm("Switch PayFast to LIVE mode? Real customer cards will be charged."))
                      modeMut.mutate("live");
                  }}
                  className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-900 disabled:opacity-50"
                >
                  Switch to Live
                </button>
              </div>
              {s.lastUpdatedAt ? (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Last changed: {new Date(s.lastUpdatedAt).toLocaleString()}
                </p>
              ) : null}
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <Activity className="h-4 w-4" /> Last 24h
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <Stat label="Total" value={s.totals.total} />
                <Stat label="Granted" value={s.totals.granted} tone="ok" />
                <Stat label="Duplicate" value={s.totals.duplicate} />
                <Stat label="Skipped" value={s.totals.skipped} />
                <Stat label="Grant failed" value={s.totals.grant_failed} tone="bad" />
                <Stat label="Bad signature" value={s.totals.signature_invalid} tone="bad" />
              </div>
            </div>
          </section>

          {/* Trace lookup */}
          {/* Guided sandbox test */}
          <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
            <h2 className="font-display text-lg text-navy">Guided sandbox test</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Run an end-to-end payment in PayFast's sandbox without charging real cards.
              The runtime mode toggle above must be set to <strong>Sandbox</strong>.
            </p>
            <ol className="mt-3 list-inside list-decimal space-y-1 text-sm text-navy">
              <li>Switch the runtime mode to <strong>Sandbox</strong>.</li>
              <li>
                Sign in as a test user whose email is <em>not</em> your PayFast merchant email
                (to avoid the "merchant cannot pay themselves" error).
              </li>
              <li>
                Start any paid flow (e.g. <span className="font-mono">/portal/credits</span> top-up).
                You'll be redirected to <span className="font-mono">sandbox.payfast.co.za</span>.
              </li>
              <li>
                Pay with PayFast's sandbox buyer:&nbsp;
                <span className="font-mono">sbtu01@payfast.io</span>, card&nbsp;
                <span className="font-mono">4000 0000 0000 0002</span>, any future expiry, CVV&nbsp;
                <span className="font-mono">123</span>.
              </li>
              <li>
                Watch the "Recent ITN deliveries" table below — your reference should appear
                within ~5s with <Pill tone="ok">granted</Pill> and matching base-hash to the
                checkout trace.
              </li>
              <li>
                If signature is invalid, paste the reference into the lookup below and compare
                the SHA-256 of the checkout trace vs. the ITN log to spot the mismatch.
              </li>
            </ol>
          </section>
          <section className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-lg text-navy">Signed-payload trace lookup</h2>
            <p className="mt-1 text-xs text-muted-foreground">

              Paste an order reference to see the masked payload we signed at checkout time
              plus its base-string SHA-256. If the same SHA appears on the ITN side, the
              signature is identical and PayFast accepted what we sent.
            </p>
            <form
              className="mt-3 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (traceRef.trim()) traceMut.mutate(traceRef.trim());
              }}
            >
              <input
                value={traceRef}
                onChange={(e) => setTraceRef(e.target.value)}
                placeholder="e.g. CR-20260628-XYZ"
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <button
                type="submit"
                disabled={traceMut.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-60"
              >
                {traceMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Lookup
              </button>
            </form>
            {trace?.trace ? (
              <TraceCard trace={trace.trace} lastItn={trace.lastItn} />
            ) : null}
          </section>

          {/* Recent deliveries */}
          <section className="rounded-2xl border border-border bg-card">
            <header className="border-b border-border p-4">
              <h2 className="font-display text-lg text-navy">Recent ITN deliveries</h2>
            </header>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-secondary/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Reference</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Sig</th>
                    <th className="px-3 py-2">VALIDATE (HTTP)</th>
                    <th className="px-3 py-2">Outcome</th>
                    <th className="px-3 py-2">Grant</th>
                    <th className="px-3 py-2">Mode</th>
                    <th className="px-3 py-2">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(s.recent ?? []).map((r: any) => (
                    <tr key={r.id} className="align-top">
                      <td className="px-3 py-2 text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px]">{r.reference ?? "—"}</td>
                      <td className="px-3 py-2">{r.payment_status ?? "—"}</td>
                      <td className="px-3 py-2">
                        {r.signature_valid === true ? (
                          <Pill tone="ok">valid</Pill>
                        ) : r.signature_valid === false ? (
                          <Pill tone="bad">invalid</Pill>
                        ) : (
                          <Pill tone="muted">—</Pill>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.server_validate_result}
                        {r.pf_response_code ? ` (${r.pf_response_code})` : ""}
                      </td>
                      <td className="px-3 py-2">
                        <Pill
                          tone={
                            r.webhook_outcome === "granted"
                              ? "ok"
                              : r.webhook_outcome === "grant_failed"
                                ? "bad"
                                : r.webhook_outcome === "duplicate"
                                  ? "muted"
                                  : "warn"
                          }
                        >
                          {r.webhook_outcome ?? "—"}
                        </Pill>
                      </td>
                      <td className="px-3 py-2">
                        {r.grant_status}
                        {r.grant_error ? (
                          <div className="mt-1 text-[10px] text-rose-700">{r.grant_error}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 uppercase">{r.mode ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-[10px]">{r.source_ip ?? "—"}</td>
                    </tr>
                  ))}
                  {(!s.recent || s.recent.length === 0) ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                        No ITN deliveries logged yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "ok" | "bad";
}) {
  const color =
    tone === "ok" ? "text-emerald-700" : tone === "bad" ? "text-rose-700" : "text-navy";
  return (
    <div>
      <p className={`font-display text-xl ${color}`}>{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

function TraceCard({ trace, lastItn }: { trace: any; lastItn: any }) {
  const itnHash = lastItn?.signed_base_hash;
  const checkoutHash = trace?.signed_base_hash;
  const match = itnHash && checkoutHash && itnHash === checkoutHash;
  return (
    <div className="mt-4 rounded-lg border border-border bg-secondary/20 p-4 text-xs">
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <p className="font-semibold text-navy">Checkout (signed by us)</p>
          <p>Mode: {trace.mode}</p>
          <p>Base SHA-256: <span className="font-mono">{trace.signed_base_hash}</span></p>
          <p>Base length: {trace.signed_base_length}</p>
          <p>Signature: <span className="font-mono">{trace.signature}</span></p>
          <p>Process URL: <span className="break-all">{trace.process_url}</span></p>
          <p>Updated: {new Date(trace.updated_at).toLocaleString()}</p>
        </div>
        <div>
          <p className="font-semibold text-navy">Last ITN attempt</p>
          {lastItn ? (
            <>
              <p>
                Signature: {lastItn.signature_valid === true ? "valid" : lastItn.signature_valid === false ? "invalid" : "—"}
              </p>
              <p>Base SHA-256: <span className="font-mono">{itnHash || "—"}</span></p>
              <p>VALIDATE: {lastItn.server_validate_result} ({lastItn.pf_response_code})</p>
              <p>Outcome: {lastItn.webhook_outcome}</p>
              {lastItn.grant_error ? <p className="text-rose-700">Error: {lastItn.grant_error}</p> : null}
              {checkoutHash ? (
                <p className={`mt-1 font-semibold ${match ? "text-emerald-700" : "text-rose-700"}`}>
                  {match ? "✓ Base hashes match" : "✗ Base hashes differ — payload changed in transit"}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-muted-foreground">No ITN attempt yet for this reference.</p>
          )}
        </div>
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer font-semibold text-navy">Masked signed fields</summary>
        <pre className="mt-2 max-h-64 overflow-auto rounded bg-white p-2 text-[11px]">
{JSON.stringify(trace.signed_field_preview, null, 2)}
        </pre>
      </details>
    </div>
  );
}
